import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Shield, Star, Crown, Zap, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../utils/tokenStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIF_ICONS = {
  role_change: { icon: Shield, color: 'text-amber-400', bg: 'bg-amber-500/20' },
  access_granted: { icon: Star, color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  friend_request: { icon: Crown, color: 'text-sky-400', bg: 'bg-sky-500/20' },
  default: { icon: Zap, color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/20' },
};

const NotificationBell = ({ isGuest }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef(null);

  const fetchNotifications = async () => {
    if (isGuest) return;
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // API returns flat list, items use is_read or read field
        const notifs = Array.isArray(data) ? data : (data.notifications || []);
        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => !n.is_read && !n.read).length);
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [isGuest]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const markAllRead = async () => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true, is_read: true })));
    } catch (e) { /* silent */ }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      markAllRead();
    }
  };

  // Add new notification from socket event
  const addNotification = (notif) => {
    setNotifications(prev => [notif, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);
  };

  // Expose addNotification for parent
  NotificationBell.addNotification = addNotification;

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (isGuest) return null;

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-white/70 hover:text-white transition-colors" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1"
              data-testid="notification-unread-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Notification Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-12 w-80 max-h-96 overflow-hidden rounded-xl border border-white/10 bg-gray-900/95 backdrop-blur-xl shadow-2xl z-50"
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-white/10">
              <span className="text-sm font-semibold text-white">Notifications</span>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-[10px] text-fuchsia-400 hover:text-fuchsia-300 flex items-center gap-1"
                    data-testid="mark-all-read-btn"
                  >
                    <Check size={10} /> Mark all read
                  </button>
                )}
                <button onClick={() => setIsOpen(false)} className="text-white/40 hover:text-white">
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto max-h-80" style={{ scrollbarWidth: 'thin' }}>
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-white/40">
                  <Bell className="w-8 h-8 mb-2 opacity-30" />
                  <p className="text-xs">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notif, idx) => {
                  const config = NOTIF_ICONS[notif.type] || NOTIF_ICONS.default;
                  const Icon = config.icon;
                  return (
                    <div
                      key={idx}
                      className={`flex items-start gap-3 p-3 border-b border-white/5 transition-colors ${
                        !notif.is_read && !notif.read ? 'bg-fuchsia-500/5' : ''
                      } hover:bg-white/5`}
                      data-testid={`notification-item-${idx}`}
                    >
                      <div className={`p-1.5 rounded-lg ${config.bg} flex-shrink-0 mt-0.5`}>
                        <Icon size={14} className={config.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-white truncate">{notif.title}</p>
                        <p className="text-[11px] text-white/50 mt-0.5">{notif.message}</p>
                        <p className="text-[10px] text-white/30 mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      {!notif.is_read && !notif.read && (
                        <div className="w-2 h-2 rounded-full bg-fuchsia-500 flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
